/**
 * Scenario Cost Comparison — same policy across locations.
 * Uses full emissions reduction (all sectors) for total reduction and portfolio cost per ton.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  BarElement,
  BarController,
  Tooltip,
  ScatterController,
  PointElement,
} from 'chart.js';
import { Bar, Scatter } from 'react-chartjs-2';
import { useLocation } from '../../contexts/LocationContext';
import { computeScenarioCostResults, type ScenarioCostResults, type ScenarioCostOptions } from '../../services/scenarioCostModel';
import {
  type CostAssumptions,
  type LocationPriceOverrides,
} from '../../services/costAssumptions';
import type { BuildingsCostAssumptions } from '../../services/buildingsCostModel';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { fetchLatestElectricityPricesForLocation } from '../../services/electricityPricesByGeographyService';
import { KeyAssumptionsTable } from './KeyAssumptionsTable';
import { getFinancialColor, getFinancialBarColor, FINANCIAL_COLORS, FINANCIAL_COLOR_NOTE } from './portfolioCostUtils';
import type { FinancialColorType } from './portfolioCostUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, Tooltip, Legend, ScatterController, PointElement);

const MAX_LOCATIONS = 5;
const MIN_LOCATIONS = 1;
const ASSUMPTIONS_DEBOUNCE_MS = 600;

/** Stable empty list so `fullLocationList` is not a new `[]` every render when data is missing. */
const EMPTY_LOCATION_LIST: string[] = [];

/** Format currency with M/B for millions/billions; use |v| for threshold so negatives show as $-X.XXM. */
const currencyShort = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1e9) return (v < 0 ? '-$' : '$') + (abs / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return (v < 0 ? '-$' : '$') + (abs / 1e6).toFixed(2) + 'M';
  return '$' + Math.round(v).toLocaleString();
};

/** Inline style for the four semantically colored columns only. */
const financialCellStyle = (value: number | null | undefined, type: FinancialColorType): React.CSSProperties => {
  const color = getFinancialColor(value, type);
  return color ? { color } : {};
};

/** Payback: "Never" = muted gray; otherwise neutral. */
const paybackCellStyle = (paybackYear: number | null | undefined): React.CSSProperties =>
  paybackYear == null ? { color: FINANCIAL_COLORS.muted } : {};

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildMergedOptions(
  parentOptions: ScenarioCostOptions | undefined,
  assumptions: CostAssumptions,
  locationOverrides: Record<string, LocationPriceOverrides>,
  locations: string[],
  iceMpg: number,
  evKwhPerMile: number,
  costView: 'incremental_vs_bau' | 'bau_costs' | 'total_costs'
): ScenarioCostOptions {
  const priceOverridesByLocation: Record<string, Partial<BuildingsCostAssumptions>> = {};
  const gasolinePriceByLocation: Record<string, number> = {};
  locations.forEach((loc) => {
    const o = locationOverrides[loc];
    priceOverridesByLocation[loc] = {
      ...parentOptions?.priceOverridesByLocation?.[loc],
      electricityPricePerKwh: o?.electricityPricePerKwh ?? assumptions.electricityPricePerKwh,
      naturalGasPricePerTherm: o?.naturalGasPricePerTherm ?? assumptions.naturalGasPricePerTherm,
      weatherizationCostPerHome: assumptions.weatherizationCostPerParticipatingHome,
      commercialRetrofitCostPerEstablishment: assumptions.commercialRetrofitCostPerParticipatingEstablishment,
      resReachIncrementalCostPerUnit: assumptions.residentialReachCodeCostPerNewUnit,
      comReachIncrementalCostPerUnit: assumptions.commercialReachCodeCostPerNewUnit,
      resHeatPumpIncrementalCostPerUnit: assumptions.resHeatPumpIncrementalCostPerUnit,
      comHeatPumpIncrementalCostPerUnit: assumptions.comHeatPumpIncrementalCostPerUnit,
      avgThermsPerResUnit: assumptions.avgThermsPerResUnit,
      avgThermsPerComEquivalentUnit: assumptions.avgThermsPerComEquivalentUnit,
      discountRate: assumptions.discountRate,
      heatPumpRebate: assumptions.heatPumpRebate,
      weatherizationRebate: assumptions.weatherizationRebate,
      commercialRetrofitRebate: assumptions.commercialRetrofitRebate,
    };
    if (o?.gasolinePricePerGallon != null && o.gasolinePricePerGallon > 0) {
      gasolinePriceByLocation[loc] = o.gasolinePricePerGallon;
    }
  });
  return {
    ...parentOptions,
    priceOverridesByLocation: { ...parentOptions?.priceOverridesByLocation, ...priceOverridesByLocation },
    gasolinePricePerGallon: assumptions.gasolinePricePerGallon,
    gasolinePriceByLocation: Object.keys(gasolinePriceByLocation).length > 0 ? gasolinePriceByLocation : undefined,
    transportElectricityPricePerKwh: assumptions.electricityPricePerKwh,
    discountRate: assumptions.discountRate,
    iceMpg,
    evKwhPerMile,
    evIncrementalCost: assumptions.evIncrementalCost,
    chargerCostPerEv: assumptions.chargerCostPerEv,
    costView,
  };
}

export interface ScenarioCostAnalysisSectionProps {
  scenario: import('../../services/policyImpactService').PolicyScenario;
  currentLocation: string;
  options?: ScenarioCostOptions;
  /** Only show when results exist (e.g. from main tool). */
  enabled?: boolean;
  /** Shared with Policy tool Cost Assumptions + portfolio cost. */
  costAssumptions: CostAssumptions;
  iceMpg: number;
  setIceMpg: React.Dispatch<React.SetStateAction<number>>;
  evKwhPerMile: number;
  setEvKwhPerMile: React.Dispatch<React.SetStateAction<number>>;
}

export const ScenarioCostAnalysisSection: React.FC<ScenarioCostAnalysisSectionProps> = ({
  scenario,
  currentLocation,
  options,
  enabled = true,
  costAssumptions,
  iceMpg,
  setIceMpg,
  evKwhPerMile,
  setEvKwhPerMile,
}) => {
  const { locationData, getCountyForCity } = useLocation();

  const fullLocationList = useMemo(() => {
    const raw = locationData?.availableLocations;
    if (raw == null || raw.length === 0) return EMPTY_LOCATION_LIST;
    return raw;
  }, [locationData?.availableLocations]);

  // Available locations = all cities in current location's county + the county + California (only names that exist in data)
  const availableLocations: string[] = useMemo(() => {
    if (fullLocationList.length === 0 || !currentLocation) return fullLocationList;
    const type = supabaseLocationService.getLocationType(currentLocation);
    const countyName = type === 'city' ? getCountyForCity(currentLocation) : type === 'county' ? currentLocation : null;
    const has = new Set(fullLocationList);
    if (!countyName) {
      // State or unknown: show California, all counties, all cities
      const counties = supabaseLocationService.getCounties().map((c) => c.name).filter((n) => has.has(n));
      const cities = supabaseLocationService.getCities().map((c) => c.name).filter((n) => has.has(n));
      return ['California', ...counties.sort(), ...cities.sort()].filter((n) => has.has(n));
    }
    const citiesInCounty = supabaseLocationService.getCitiesInCounty(countyName);
    const base = [countyName, 'California', ...citiesInCounty].filter((n) => has.has(n));
    return Array.from(new Set(base));
  }, [currentLocation, fullLocationList, getCountyForCity]);

  // All cities and all counties for the optional "Add location" dropdown
  const allCitiesAndCounties = useMemo(() => {
    const has = new Set(fullLocationList);
    const counties = supabaseLocationService.getCounties().map((c) => c.name).filter((n) => has.has(n)).sort();
    const cities = supabaseLocationService.getCities().map((c) => c.name).filter((n) => has.has(n)).sort();
    const california = has.has('California') ? ['California'] : [];
    return { counties: [...california, ...counties], cities };
  }, [fullLocationList]);

  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [locationOverrides, setLocationOverrides] = useState<Record<string, LocationPriceOverrides>>({});
  const [costView, setCostView] = useState<'incremental_vs_bau' | 'bau_costs' | 'total_costs'>('incremental_vs_bau');
  const [results, setResults] = useState<ScenarioCostResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [normalizePerCapita, setNormalizePerCapita] = useState(true);
  const [sensitivityResults, setSensitivityResults] = useState<{
    byLocation: Record<string, { costPerTonBase: number | null; costPerTonMin: number; costPerTonMax: number }>;
  } | null>(null);
  const [sensitivityLoading, setSensitivityLoading] = useState(false);

  /** Primitive for effect deps (avoids complex-expression / missing-length exhaustive-deps warnings). */
  const selectedLocationCount = selectedLocations.length;

  // Populate location-specific electricity (and gas) from DB for every selected location so cost uses correct prices per location
  useEffect(() => {
    const locs = selectedLocations.filter(Boolean);
    if (locs.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates: Record<string, Partial<LocationPriceOverrides>> = {};
      for (const loc of locs) {
        try {
          const geofips = await supabaseLocationService.getGeofips(loc);
          if (cancelled || geofips == null) continue;
          const prices = await fetchLatestElectricityPricesForLocation(geofips);
          if (cancelled) return;
          const buildingPrice = prices.buildingPricePerKwh ?? prices.average;
          if (buildingPrice != null && buildingPrice > 0) {
            updates[loc] = { ...updates[loc], electricityPricePerKwh: buildingPrice };
          }
        } catch {
          /* skip this location */
        }
      }
      if (cancelled) return;
      if (Object.keys(updates).length > 0) {
        setLocationOverrides((prev) => {
          let changed = false;
          const next = { ...prev };
          for (const [loc, u] of Object.entries(updates)) {
            const merged = { ...prev[loc], ...u };
            const p = prev[loc];
            if (
              p?.electricityPricePerKwh === merged.electricityPricePerKwh &&
              p?.naturalGasPricePerTherm === merged.naturalGasPricePerTherm &&
              p?.gasolinePricePerGallon === merged.gasolinePricePerGallon
            ) {
              continue;
            }
            changed = true;
            next[loc] = merged;
          }
          return changed ? next : prev;
        });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLocations]);

  // Default: only current location and California (users can add more up to MAX_LOCATIONS)
  useEffect(() => {
    if (fullLocationList.length === 0 || !currentLocation) return;
    const inList = (name: string) => fullLocationList.some((l) => l === name);
    const parts: string[] = [];
    if (inList(currentLocation)) parts.push(currentLocation);
    if (inList('California')) parts.push('California');
    const valid = Array.from(new Set(parts)).filter(inList);
    const next = valid.slice(0, MAX_LOCATIONS);
    setSelectedLocations((prev) =>
      prev.length === next.length && next.every((name, i) => prev[i] === name) ? prev : next,
    );
  }, [currentLocation, fullLocationList]);

  const runComparison = useCallback(async () => {
    const locs = selectedLocations.filter(Boolean);
    if (locs.length < MIN_LOCATIONS) {
      setError(MIN_LOCATIONS === 1 ? 'Select at least one location.' : `Select at least ${MIN_LOCATIONS} locations.`);
      return;
    }
    if (locs.length > MAX_LOCATIONS) {
      setError(`Select at most ${MAX_LOCATIONS} locations.`);
      return;
    }
    setLoading(true);
    setError(null);
    const merged = buildMergedOptions(options, costAssumptions, locationOverrides, locs, iceMpg, evKwhPerMile, costView);
    try {
      const out = await computeScenarioCostResults({
        scenario,
        locations: locs,
        options: merged,
      });
      setResults(out);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to compute scenario cost.');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [scenario, selectedLocations, options, costAssumptions, locationOverrides, iceMpg, evKwhPerMile, costView]);

  /** Sorted join — string key for effects (order-independent). */
  const locationKey = useMemo(
    () => [...selectedLocations].sort().join(','),
    [selectedLocations]
  );

  useEffect(() => {
    if (!enabled || selectedLocations.length < MIN_LOCATIONS) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const merged = buildMergedOptions(options, costAssumptions, locationOverrides, selectedLocations, iceMpg, evKwhPerMile, costView);
    computeScenarioCostResults({ scenario, locations: selectedLocations, options: merged })
      .then((out) => { if (!cancelled) setResults(out); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to compute scenario cost.'); setResults(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Refetch when locations or cost view change. Omit scenario/options/assumptions so assumption edits go through debounced effect only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, locationKey, costView]);

  const assumptionsDebounceRan = useRef(false);
  useEffect(() => {
    if (!enabled || selectedLocationCount < MIN_LOCATIONS) return;
    if (!assumptionsDebounceRan.current) {
      assumptionsDebounceRan.current = true;
      return;
    }
    const id = setTimeout(() => runComparison(), ASSUMPTIONS_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [
    costAssumptions,
    locationOverrides,
    iceMpg,
    evKwhPerMile,
    costView,
    enabled,
    runComparison,
    selectedLocationCount,
  ]);

  const toggleLocation = (loc: string) => {
    setSelectedLocations((prev) => {
      const has = prev.includes(loc);
      if (has) {
        const next = prev.filter((l) => l !== loc);
        return next.length >= MIN_LOCATIONS ? next : prev;
      }
      if (prev.length >= MAX_LOCATIONS) return prev;
      return [...prev, loc];
    });
  };

  /** Format y-axis ticks without scientific notation — stable ref for Chart.js options. */
  const yAxisTickCallback = useCallback((value: string | number) => {
    return typeof value === 'number'
      ? value.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 })
      : value;
  }, []);

  const barChartOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx) => currencyShort(Number(ctx.parsed.y)) } },
      },
      scales: {
        x: { title: { display: true, text: 'Location' } },
        y: { beginAtZero: true, ticks: { callback: yAxisTickCallback } },
      },
    }),
    [yAxisTickCallback],
  );

  const {
    tableRows,
    labels,
    buildingsUpfrontData,
    transportUpfrontData,
    upfrontData,
    npvData,
    upfrontDataNorm,
    npvDataNorm,
    buildingsUpfrontDataNorm,
    transportUpfrontDataNorm,
    scatterData,
  } = useMemo(() => {
    const rows = results ? Object.entries(results.byLocation) : [];
    const popForLoc = (r: (typeof rows)[0][1]) => (r.population != null && r.population > 0 ? r.population : 1);
    const reductionPerCapitaTons = (r: (typeof rows)[0][1]) =>
      r.population != null && r.population > 0 ? r.totalEmissionsReductionMT / r.population : 0;
    return {
      tableRows: rows,
      labels: rows.map(([loc]) => loc),
      buildingsUpfrontData: rows.map(([, r]) => r.buildings.costEffectivenessMetrics.upfrontCapitalCost),
      transportUpfrontData: rows.map(([, r]) => r.transport.upfrontCapitalCost),
      upfrontData: rows.map(([, r]) => r.totalUpfrontCost),
      npvData: rows.map(([, r]) => r.totalNPV),
      upfrontDataNorm: rows.map(([, r]) => r.totalUpfrontCost / popForLoc(r)),
      npvDataNorm: rows.map(([, r]) => r.totalNPV / popForLoc(r)),
      buildingsUpfrontDataNorm: rows.map(([, r]) => r.buildings.costEffectivenessMetrics.upfrontCapitalCost / popForLoc(r)),
      transportUpfrontDataNorm: rows.map(([, r]) => r.transport.upfrontCapitalCost / popForLoc(r)),
      scatterData: rows.map(([loc, r]) => ({
        x: r.costPerTon ?? 0,
        y: normalizePerCapita ? reductionPerCapitaTons(r) : r.totalEmissionsReductionMT,
        loc,
        costPerTon: r.costPerTon,
        reduction: r.totalEmissionsReductionMT,
        reductionPerCapitaTons: reductionPerCapitaTons(r),
      })),
    };
  }, [results, normalizePerCapita]);

  /** Stable Chart.js props — inline objects force react-chartjs-2 to diff/reinit every parent render. */
  const sectorStackedBarData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Buildings',
          data: normalizePerCapita ? buildingsUpfrontDataNorm : buildingsUpfrontData,
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          stack: 'stack0' as const,
        },
        {
          label: 'Transport',
          data: normalizePerCapita ? transportUpfrontDataNorm : transportUpfrontData,
          backgroundColor: 'rgba(34, 197, 94, 0.8)',
          stack: 'stack0' as const,
        },
      ],
    }),
    [
      labels,
      normalizePerCapita,
      buildingsUpfrontData,
      buildingsUpfrontDataNorm,
      transportUpfrontData,
      transportUpfrontDataNorm,
    ],
  );

  const stackedSectorBarOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      ...barChartOptions,
      scales: {
        ...barChartOptions.scales,
        x: { ...barChartOptions.scales?.x, stacked: true },
        y: {
          ...barChartOptions.scales?.y,
          stacked: true,
          title: { display: true, text: normalizePerCapita ? '$/capita' : undefined },
        },
      },
      plugins: {
        ...barChartOptions.plugins,
        legend: { display: true },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              `${ctx.dataset.label}: ${currencyShort(Number(ctx.parsed.y))}${normalizePerCapita ? ' per capita' : ''}`,
          },
        },
      },
    }),
    [barChartOptions, normalizePerCapita],
  );

  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.count('ScenarioCostAnalysisSection:render');
  }
  if (!enabled) return null;

  return (
    <section id="scenario-cost-analysis-section" className="scenario-cost-comparison-section policy-tool-cost-section-shell">
      <details className="policy-tool-disclosure">
        <summary className="policy-tool-disclosure-summary">
          <span style={{ marginRight: 8 }}>▶</span> Scenario Cost Comparison Across Locations
        </summary>
        <div className="policy-tool-disclosure-body">
          <p className="policy-tool-text-muted" style={{ fontSize: '0.875rem', marginTop: 12, marginBottom: 16 }}>
            Compare financial impacts and emissions reduction of the same policy scenario across locations.
          </p>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 48, marginBottom: 24 }}>
          <div className="loading-spinner" style={{ marginBottom: 0 }} aria-hidden />
          <span className="policy-tool-text-muted" style={{ fontSize: '0.9375rem' }}>
            Loading comparison…
          </span>
        </div>
      )}
      {results && tableRows.length > 0 && !loading && (
        <div className="policy-tool-info-panel" style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: '0.9375rem', marginBottom: 10, fontWeight: 600 }}>How costs and savings are calculated</h4>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.8125rem', lineHeight: 1.6 }} className="policy-tool-text-body">
            <li><strong>Period:</strong> All costs and savings are <strong>cumulative over 2023–2045</strong>. Upfront, NPV, cumulative net cost, and fuel savings sum or discount over this window.</li>
            <li><strong>Baseline (BAU):</strong> The baseline already includes state policy (e.g. state EV adoption ramp, grid decarbonization, building codes). Numbers in this table are <strong>incremental to that BAU</strong>—i.e. the extra cost or savings from the local scenario you selected, not the full cost/savings from state policy.</li>
            <li><strong>Buildings:</strong> Upfront = capital (existing residential retrofit, existing commercial retrofit, new residential construction standards, heat pumps, etc.). Fuel savings = cumulative reduction in utility bills (gas + electricity) from efficiency and electrification vs BAU. Electricity cost stays in buildings; transport EV charging is modeled separately.</li>
            <li><strong>Transport:</strong> Upfront = EV incremental cost + charger cost (no cost for VMT reduction). Fuel savings = net of gasoline savings and EV electricity cost: gasoline use drops (from EV adoption and VMT reduction), while electricity for EVs increases. <strong>VMT reduction is pure savings</strong> (less gasoline, no extra cost in this model). Gasoline savings and EV electricity cost are shown in the sector breakdown.</li>
            <li><strong>Emissions reduction:</strong> Cumulative MT CO₂e (baseline − policy) over 2023–2045, all sectors.</li>
          </ul>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: 8 }}>Locations (select 2–5)</label>
        <p className="policy-tool-text-muted" style={{ fontSize: '0.8125rem', marginBottom: 8 }}>
          {availableLocations.length > 0 ? 'Cities in county, county, and California.' : 'Loading…'}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {availableLocations.map((loc) => (
            <label key={loc} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedLocations.includes(loc)}
                onChange={() => toggleLocation(loc)}
                disabled={!selectedLocations.includes(loc) && selectedLocations.length >= MAX_LOCATIONS}
              />
              <span style={{ fontSize: '0.875rem' }}>{loc}</span>
            </label>
          ))}
        </div>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Add location:</label>
          <select
            value=""
            onChange={(e) => {
              const name = e.target.value;
              if (!name) return;
              e.target.value = '';
              if (!selectedLocations.includes(name) && selectedLocations.length < MAX_LOCATIONS) {
                setSelectedLocations((prev) => [...prev, name]);
              }
            }}
            style={{ padding: '6px 10px', fontSize: '0.875rem', minWidth: 200, borderRadius: 4 }}
          >
            <option value="">— All cities and counties —</option>
            <optgroup label="Counties">
              {allCitiesAndCounties.counties.map((name) => (
                <option key={name} value={name} disabled={selectedLocations.includes(name)}>
                  {name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Cities">
              {allCitiesAndCounties.cities.map((name) => (
                <option key={name} value={name} disabled={selectedLocations.includes(name)}>
                  {name}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <button
          type="button"
          onClick={runComparison}
          disabled={loading || selectedLocations.length < MIN_LOCATIONS}
          style={{ marginTop: 12, padding: '8px 16px', cursor: loading ? 'wait' : 'pointer' }}
        >
          {loading ? 'Loading…' : 'Update comparison'}
        </button>
        {error && <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: 8 }}>{error}</p>}
      </div>

      <details className="policy-tool-nested-panel">
        <summary className="policy-tool-disclosure-summary" style={{ padding: '8px 0', fontSize: '0.9375rem' }}>
          Per-location energy price overrides (optional)
        </summary>
        <p className="policy-tool-text-muted" style={{ fontSize: '0.8125rem', marginTop: 8, marginBottom: 12 }}>
          Global defaults are set in <strong>Cost Assumptions</strong> below. Leave cells blank here to use those values for each location in this comparison.
        </p>

        {selectedLocations.length > 0 && (
          <>
            <div className="policy-tool-form-group-title">Selected locations</div>
            <p className="policy-tool-text-muted" style={{ fontSize: '0.75rem', marginBottom: 8 }}>
              Leave blank to use global default.
            </p>
            <div style={{ overflowX: 'auto', marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 6 }}>Location</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>Electricity ($/kWh)</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>Natural gas ($/therm)</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>Gasoline ($/gal)</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedLocations.map((loc) => (
                    <tr key={loc}>
                      <td style={{ padding: 6 }}>{loc}</td>
                      <td style={{ padding: 6 }}>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          placeholder={String(costAssumptions.electricityPricePerKwh)}
                          value={locationOverrides[loc]?.electricityPricePerKwh ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                            setLocationOverrides((prev) => ({
                              ...prev,
                              [loc]: { ...prev[loc], electricityPricePerKwh: v },
                            }));
                          }}
                          style={{ width: 90, padding: '2px 6px' }}
                        />
                      </td>
                      <td style={{ padding: 6 }}>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          placeholder={String(costAssumptions.naturalGasPricePerTherm)}
                          value={locationOverrides[loc]?.naturalGasPricePerTherm ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                            setLocationOverrides((prev) => ({
                              ...prev,
                              [loc]: { ...prev[loc], naturalGasPricePerTherm: v },
                            }));
                          }}
                          style={{ width: 90, padding: '2px 6px' }}
                        />
                      </td>
                      <td style={{ padding: 6 }}>
                        <input
                          type="number"
                          min={0}
                          step={0.1}
                          placeholder={String(costAssumptions.gasolinePricePerGallon)}
                          value={locationOverrides[loc]?.gasolinePricePerGallon ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                            setLocationOverrides((prev) => ({
                              ...prev,
                              [loc]: { ...prev[loc], gasolinePricePerGallon: v },
                            }));
                          }}
                          style={{ width: 90, padding: '2px 6px' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <button
            type="button"
            onClick={() => setLocationOverrides({})}
            style={{ padding: '6px 12px', fontSize: '0.8125rem' }}
          >
            Clear per-location overrides
          </button>
          <button
            type="button"
            onClick={() => {
              const next: Record<string, LocationPriceOverrides> = {};
              selectedLocations.forEach((loc) => {
                next[loc] = {
                  electricityPricePerKwh: costAssumptions.electricityPricePerKwh,
                  naturalGasPricePerTherm: costAssumptions.naturalGasPricePerTherm,
                  gasolinePricePerGallon: costAssumptions.gasolinePricePerGallon,
                };
              });
              setLocationOverrides(next);
            }}
            style={{ padding: '6px 12px', fontSize: '0.8125rem' }}
          >
            Apply global values to all locations
          </button>
        </div>
      </details>

      <details className="policy-tool-nested-panel">
        <summary className="policy-tool-disclosure-summary" style={{ padding: '8px 0', fontSize: '0.9375rem' }}>
          Key assumptions (BAU) by scenario
        </summary>
        <div style={{ marginTop: 12 }}>
          {currentLocation && (
            <KeyAssumptionsTable
              location={currentLocation}
              scenario={scenario}
              enabled={!!results && tableRows.length > 0}
              options={options}
              embedded
            />
          )}
        </div>
      </details>

      {results && tableRows.length > 0 && !loading && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <h3 className="policy-tool-heading-sm">Summary by location</h3>
            <label
              className="policy-tool-text-muted"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem' }}
            >
              <span>Cost view:</span>
              <select
                value={costView}
                onChange={(e) => setCostView(e.target.value as 'incremental_vs_bau' | 'bau_costs' | 'total_costs')}
                style={{ padding: '4px 8px', borderRadius: 4 }}
              >
                <option value="incremental_vs_bau">Costs above BAU (incremental local policy)</option>
                <option value="bau_costs">BAU costs (state policy alone vs flat baseline)</option>
                <option value="total_costs">Total costs (state + local vs flat baseline)</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() => {
                const header = ['location', 'totalUpfrontCost', 'totalNPV', 'totalCumulativeNetCost', 'totalSavings', 'totalEmissionsReductionTons', 'costPerTon', 'paybackYear', 'costPerCapita', 'reductionPerCapitaTons'];
                const rows = tableRows.map(([loc, r]) => {
                  const totalSavings = r.buildings.annualSavingsSeries.reduce((s, x) => s + x.value, 0) + r.transport.breakdown.fuelSavings;
                  return [
                    loc,
                    r.totalUpfrontCost,
                    r.totalNPV,
                    r.totalCumulativeNetCost,
                    totalSavings,
                    Math.round(r.totalEmissionsReductionMT),
                    r.costPerTon != null ? r.costPerTon.toFixed(2) : '',
                    r.paybackYear != null ? String(r.paybackYear) : 'Never',
                    r.population != null && r.population > 0 ? (r.totalCumulativeNetCost / r.population).toFixed(2) : '',
                    r.population != null && r.population > 0 ? (r.totalEmissionsReductionMT / r.population).toFixed(2) : '',
                  ];
                });
                const csv = [header.join(','), ...rows.map((row) => row.map(escapeCsvCell).join(','))].join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'scenario-cost-comparison.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}
              style={{ padding: '6px 12px', fontSize: '0.875rem' }}
            >
              Download comparison table
            </button>
          </div>
          <p style={{ fontSize: '0.75rem', color: FINANCIAL_COLORS.muted, marginBottom: 4 }}>
            {FINANCIAL_COLOR_NOTE}
          </p>
          <p className="policy-tool-text-muted" style={{ fontSize: '0.8125rem', marginBottom: 8 }}>
            Sign conventions: <strong>Cumulative net cost</strong> = undiscounted (costs − savings) over 2023–2045; negative means net savings. <strong>NPV</strong> = present value of (savings − costs); positive = net benefit. <strong>Cost per ton</strong> negative = net savings per ton reduced. <strong>Payback year</strong> = first year when cumulative net cost ≤ 0; only shown if cumulative net cost is ≤ 0 by 2045, otherwise <strong>Never</strong>.
          </p>
          <div style={{ overflowX: 'auto', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8 }}>Location</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Upfront Cost</th>
                  <th style={{ textAlign: 'right', padding: 8 }} title="Net present value of (savings − costs). Positive = net benefit.">NPV</th>
                  <th style={{ textAlign: 'right', padding: 8 }} title="Undiscounted sum of (costs − savings) over 2023–2045. Negative = net savings.">Cumulative Net Cost</th>
                  <th style={{ textAlign: 'right', padding: 8 }} title="Cumulative operating savings (buildings) and fuel savings (transport) over 2023–2045.">Savings</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Emissions Reduction (tons)</th>
                  <th style={{ textAlign: 'right', padding: 8 }} title="Cumulative net cost ÷ total emissions reduction. Negative = net savings per ton.">Portfolio cost per ton reduced</th>
                  <th style={{ textAlign: 'right', padding: 8 }} title="First year when cumulative net cost ≤ 0 (costs − savings). Only shown if cumulative net cost is ≤ 0 by 2045; otherwise Never.">Payback Year</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Cost per Capita</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Reduction per Capita (tons)</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map(([loc, r]) => {
                  const b = r.buildings.costEffectivenessMetrics;
                  const t = r.transport;
                  const pop = r.population != null && r.population > 0 ? r.population : null;
                  const buildingsSavings = r.buildings.annualSavingsSeries.reduce((s, x) => s + x.value, 0);
                  const transportFuelSavings = t.breakdown.fuelSavings;
                  const totalSavings = buildingsSavings + transportFuelSavings;
                  return (
                    <React.Fragment key={loc}>
                      {/* Total row */}
                      <tr style={{ fontWeight: 600 }}>
                        <td className="policy-tool-fin-neutral" style={{ padding: 8 }}>
                          {loc}
                        </td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {currencyShort(r.totalUpfrontCost)}
                        </td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(r.totalNPV, 'npv') }} title="Positive = net benefit; negative = net cost">{currencyShort(r.totalNPV)}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(r.totalCumulativeNetCost, 'cumulative') }} title="Negative = net savings">{currencyShort(r.totalCumulativeNetCost)}</td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {currencyShort(totalSavings)}
                        </td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {Math.round(r.totalEmissionsReductionMT).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(r.costPerTon ?? undefined, 'cost_per_ton') }} title="Negative = net savings per ton">{r.costPerTon != null ? currencyShort(r.costPerTon) : '—'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...paybackCellStyle(r.paybackYear) }}>{r.paybackYear != null ? String(r.paybackYear) : 'Never'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(pop ? r.totalCumulativeNetCost / pop : undefined, 'cost_per_capita') }}>{pop ? currencyShort(r.totalCumulativeNetCost / pop) : '—'}</td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {pop ? (r.totalEmissionsReductionMT / pop).toFixed(2) : '—'}
                        </td>
                      </tr>
                      {/* Buildings row */}
                      <tr className="policy-tool-table-subrow">
                        <td className="policy-tool-fin-neutral" style={{ padding: 8, paddingLeft: 24, fontSize: '0.8125rem' }}>
                          Buildings
                        </td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {currencyShort(b.upfrontCapitalCost)}
                        </td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(b.netPresentValue, 'npv') }}>{currencyShort(b.netPresentValue)}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(b.cumulativeNetCost, 'cumulative') }}>{currencyShort(b.cumulativeNetCost)}</td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {currencyShort(buildingsSavings)}
                        </td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {Math.round(r.buildings.totalBuildingEmissionsReductionMT).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(b.costPerTonCO2eReduced ?? undefined, 'cost_per_ton') }}>{b.costPerTonCO2eReduced != null ? currencyShort(b.costPerTonCO2eReduced) : '—'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...paybackCellStyle(b.paybackYear) }}>{b.paybackYear != null ? String(b.paybackYear) : 'Never'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(pop ? b.cumulativeNetCost / pop : undefined, 'cost_per_capita') }}>{pop ? currencyShort(b.cumulativeNetCost / pop) : '—'}</td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {pop ? (r.buildings.totalBuildingEmissionsReductionMT / pop).toFixed(2) : '—'}
                        </td>
                      </tr>
                      {/* Transport row */}
                      <tr className="policy-tool-table-subrow">
                        <td className="policy-tool-fin-neutral" style={{ padding: 8, paddingLeft: 24, fontSize: '0.8125rem' }}>
                          Transport
                        </td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {currencyShort(t.upfrontCapitalCost)}
                        </td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(t.netPresentValue, 'npv') }}>{currencyShort(t.netPresentValue)}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(t.cumulativeNetCost, 'cumulative') }}>{currencyShort(t.cumulativeNetCost)}</td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {currencyShort(transportFuelSavings)}
                        </td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {Math.round(t.emissionsReductionMT).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(t.costPerTon ?? undefined, 'cost_per_ton') }}>{t.costPerTon != null ? currencyShort(t.costPerTon) : '—'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...paybackCellStyle(t.paybackYear) }}>{t.paybackYear != null ? String(t.paybackYear) : 'Never'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(pop ? t.cumulativeNetCost / pop : undefined, 'cost_per_capita') }}>{pop ? currencyShort(t.cumulativeNetCost / pop) : '—'}</td>
                        <td className="policy-tool-fin-neutral" style={{ textAlign: 'right', padding: 8 }}>
                          {pop ? (t.emissionsReductionMT / pop).toFixed(2) : '—'}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={normalizePerCapita}
                onChange={(e) => setNormalizePerCapita(e.target.checked)}
              />
              Normalize per capita
            </label>
            <span className="policy-tool-text-muted" style={{ fontSize: '0.8125rem' }}>
              When on, charts show per-capita values.
            </span>
          </div>

          <h3 className="policy-tool-heading-sm" style={{ marginBottom: 8 }}>
            {normalizePerCapita ? 'Cost breakdown by sector (per capita)' : 'Cost breakdown by sector'}
          </h3>
          <div style={{ height: 220, marginBottom: 24 }}>
            <Bar data={sectorStackedBarData} options={stackedSectorBarOptions} />
          </div>

          <h3 className="policy-tool-heading-sm" style={{ marginBottom: 8 }}>
            {normalizePerCapita ? 'Upfront cost per capita by location' : 'Upfront cost by location'}
          </h3>
          <div style={{ height: 220, marginBottom: 24 }}>
            <Bar
              data={{ labels, datasets: [{ label: normalizePerCapita ? 'Upfront $/capita' : 'Upfront cost', data: normalizePerCapita ? upfrontDataNorm : upfrontData, backgroundColor: 'rgba(59, 130, 246, 0.7)' }] }}
              options={barChartOptions}
            />
          </div>

          <h3 className="policy-tool-heading-sm" style={{ marginBottom: 8 }}>
            Cost vs Emissions Reduction
          </h3>
          <div style={{ height: 280, marginBottom: 24 }}>
            <Scatter
              data={{
                datasets: [{
                  label: 'Locations',
                  data: scatterData,
                  backgroundColor: 'rgba(59, 130, 246, 0.7)',
                  borderColor: 'rgba(59, 130, 246, 1)',
                  pointRadius: 8,
                }],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const raw = ctx.raw as { loc?: string; costPerTon?: number | null; reduction?: number; reductionPerCapitaTons?: number };
                        const loc = raw?.loc ?? ctx.label;
                        const cp = raw?.costPerTon != null ? currencyShort(raw.costPerTon) : '—';
                        const red = normalizePerCapita && raw?.reductionPerCapitaTons != null ? `${raw.reductionPerCapitaTons.toFixed(2)} tons/capita` : raw?.reduction != null ? `${Math.round(raw.reduction).toLocaleString(undefined, { maximumFractionDigits: 0 })} tons` : '—';
                        return [loc, `Cost per ton: ${cp}`, normalizePerCapita ? `Reduction per capita: ${red}` : `Total reduction: ${red}`];
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    title: { display: true, text: 'Portfolio cost per ton ($/tCO₂e)' },
                    beginAtZero: true,
                  },
                  y: {
                    title: { display: true, text: normalizePerCapita ? 'Emissions reduction per capita (tons)' : 'Total emissions reduction (tons)' },
                    beginAtZero: true,
                  },
                },
              }}
            />
          </div>

          <h3 className="policy-tool-heading-sm" style={{ marginBottom: 8 }}>
            {normalizePerCapita ? 'NPV per capita by location' : 'NPV by location'}
          </h3>
          <div style={{ height: 220, marginBottom: 24 }}>
            <Bar
              data={{ labels, datasets: [{ label: normalizePerCapita ? 'NPV $/capita' : 'NPV', data: normalizePerCapita ? npvDataNorm : npvData, backgroundColor: 'rgba(124, 58, 237, 0.7)' }] }}
              options={barChartOptions}
            />
          </div>

          <details className="policy-tool-nested-panel" style={{ marginBottom: 24 }}>
            <summary className="policy-tool-disclosure-summary" style={{ padding: '8px 0', fontSize: '0.9375rem' }}>
              Sensitivity analysis
            </summary>
            <p className="policy-tool-text-muted" style={{ fontSize: '0.8125rem', marginTop: 8, marginBottom: 12 }}>
              Test cost per ton with gasoline and electricity prices ±20%. Runs 5 scenarios (base, gas +20%, gas −20%, elec +20%, elec −20%).
            </p>
            <button
              type="button"
              disabled={sensitivityLoading || selectedLocations.length < MIN_LOCATIONS}
              onClick={async () => {
                setSensitivityLoading(true);
                try {
                  const locs = selectedLocations.filter(Boolean);
                  const merged = buildMergedOptions(options, costAssumptions, locationOverrides, locs, iceMpg, evKwhPerMile, costView);
                  const base = await computeScenarioCostResults({ scenario, locations: locs, options: merged });
                  const gasUp = await computeScenarioCostResults({
                    scenario,
                    locations: locs,
                    options: { ...merged, gasolinePricePerGallon: (merged.gasolinePricePerGallon ?? costAssumptions.gasolinePricePerGallon) * 1.2 },
                  });
                  const gasDown = await computeScenarioCostResults({
                    scenario,
                    locations: locs,
                    options: { ...merged, gasolinePricePerGallon: (merged.gasolinePricePerGallon ?? costAssumptions.gasolinePricePerGallon) * 0.8 },
                  });
                  const elecUp = await computeScenarioCostResults({
                    scenario,
                    locations: locs,
                    options: {
                      ...merged,
                      priceOverridesByLocation: Object.fromEntries(
                        locs.map((loc) => [loc, { ...merged.priceOverridesByLocation?.[loc], electricityPricePerKwh: (merged.priceOverridesByLocation?.[loc]?.electricityPricePerKwh ?? costAssumptions.electricityPricePerKwh) * 1.2 }])
                      ),
                      transportElectricityPricePerKwh: costAssumptions.electricityPricePerKwh * 1.2,
                    },
                  });
                  const elecDown = await computeScenarioCostResults({
                    scenario,
                    locations: locs,
                    options: {
                      ...merged,
                      priceOverridesByLocation: Object.fromEntries(
                        locs.map((loc) => [loc, { ...merged.priceOverridesByLocation?.[loc], electricityPricePerKwh: (merged.priceOverridesByLocation?.[loc]?.electricityPricePerKwh ?? costAssumptions.electricityPricePerKwh) * 0.8 }])
                      ),
                      transportElectricityPricePerKwh: costAssumptions.electricityPricePerKwh * 0.8,
                    },
                  });
                  const byLocation: Record<string, { costPerTonBase: number | null; costPerTonMin: number; costPerTonMax: number }> = {};
                  locs.forEach((loc) => {
                    const vals = [
                      base.byLocation[loc]?.costPerTon,
                      gasUp.byLocation[loc]?.costPerTon,
                      gasDown.byLocation[loc]?.costPerTon,
                      elecUp.byLocation[loc]?.costPerTon,
                      elecDown.byLocation[loc]?.costPerTon,
                    ].filter((x): x is number => x != null && Number.isFinite(x));
                    const baseVal = base.byLocation[loc]?.costPerTon ?? null;
                    byLocation[loc] = {
                      costPerTonBase: baseVal,
                      costPerTonMin: vals.length > 0 ? Math.min(...vals) : 0,
                      costPerTonMax: vals.length > 0 ? Math.max(...vals) : 0,
                    };
                  });
                  setSensitivityResults({ byLocation });
                } catch (e) {
                  setSensitivityResults(null);
                } finally {
                  setSensitivityLoading(false);
                }
              }}
              style={{ padding: '6px 12px', fontSize: '0.875rem', marginBottom: 12 }}
            >
              {sensitivityLoading ? 'Running…' : 'Run sensitivity analysis'}
            </button>
            {sensitivityResults && (() => {
              const locs = Object.keys(sensitivityResults.byLocation);
              const sensLabels = locs;
              const minData = locs.map((loc) => sensitivityResults.byLocation[loc].costPerTonMin);
              const baseData = locs.map((loc) => sensitivityResults.byLocation[loc].costPerTonBase ?? 0);
              const maxData = locs.map((loc) => sensitivityResults.byLocation[loc].costPerTonMax);
              const colorFor = (v: number) => {
        const base = getFinancialBarColor(v);
        if (base === FINANCIAL_COLORS.favorable) return 'rgba(15, 159, 110, 0.85)';
        if (base === FINANCIAL_COLORS.unfavorable) return 'rgba(199, 91, 57, 0.85)';
        return 'rgba(107, 114, 128, 0.7)';
      };
              return (
                <div style={{ marginTop: 12 }}>
                  <h4 style={{ fontSize: '0.9375rem', marginBottom: 8, color: 'var(--text-primary)' }}>Cost per ton: Min / Base / Max</h4>
                  <p className="policy-tool-text-muted" style={{ fontSize: '0.8125rem', marginBottom: 8 }}>
                    Green = net savings per ton; red = net cost per ton. Tooltip: negative = net savings.
                  </p>
                  <div style={{ height: 220 }}>
                    <Bar
                      data={{
                        labels: sensLabels,
                        datasets: [
                          { label: 'Min', data: minData, backgroundColor: minData.map(colorFor) },
                          { label: 'Base', data: baseData, backgroundColor: baseData.map(colorFor) },
                          { label: 'Max', data: maxData, backgroundColor: maxData.map(colorFor) },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'bottom' },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => `${ctx.dataset.label}: ${currencyShort(Number(ctx.parsed.y))} (negative = net savings)`,
                            },
                          },
                        },
                        scales: {
                          x: { title: { display: true, text: 'Location' } },
                          y: {
                            title: { display: true, text: 'Cost per ton ($/tCO₂e)' },
                            ticks: { callback: (v) => (typeof v === 'number' ? currencyShort(v) : v) },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              );
            })()}
          </details>
        </>
      )}

      {results && tableRows.length === 0 && !loading && (
        <p className="policy-tool-text-muted" style={{ fontSize: '0.875rem' }}>
          No cost data for selected locations. Ensure at least 2 locations are selected and run comparison.
        </p>
      )}
        </div>
      </details>
    </section>
  );
};
